const dmvLocations = [
  {
    name: "Broward County Tax Collector — Main Office",
    address: "115 S Andrews Ave, Fort Lauderdale, FL 33301",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "Driver licenses, REAL ID, vehicle titles & registration. Appointment recommended. Serving all Broward County residents including Pompano Beach.",
    featured: true,
  },
  {
    name: "Broward County Tax Collector — North Regional",
    address: "3700 NW 10th Ave, Pompano Beach, FL 33064",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "Closest DMV office to Pompano Beach residents. Call ahead for current wait times and REAL ID appointments.",
    featured: false,
  },
  {
    name: "Broward County Tax Collector — Deerfield Beach",
    address: "1600 W Hillsboro Blvd, Deerfield Beach, FL 33442",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "Northern Broward County option. Convenient for Lighthouse Point and Deerfield Beach area residents.",
    featured: false,
  },
];

export const landmarks = [
  {
    name: "Pompano Beach Fishing Pier",
    icon: "ri-anchor-line",
    distance: "0 miles — located on Pompano Beach shoreline",
    desc: "Iconic 1,080-foot pier extending into the Atlantic Ocean. A defining landmark of Pompano Beach and a major attraction for visitors.",
    url: "https://www.pompanobeachfl.gov/",
    schemaType: "TouristAttraction",
  },
  {
    name: "Hillsboro Inlet",
    icon: "ri-ship-line",
    distance: "3 miles north — between Pompano Beach and Hillsboro Beach",
    desc: "Natural inlet connecting the Intracoastal Waterway to the Atlantic Ocean. Primary access point for Pompano Beach fishing charters, private boats, and recreational vessels heading to the Bahamas.",
    url: "https://www.pompanobeachfl.gov/",
    schemaType: "TouristAttraction",
  },
  {
    name: "Port Everglades",
    icon: "ri-anchor-line",
    distance: "15 miles south in Fort Lauderdale",
    desc: "One of the world's busiest cruise ports. Royal Caribbean, Celebrity, Holland America, Princess, and Seabourn depart regularly for the Caribbean, Panama Canal, and transatlantic itineraries. All cruise passengers require valid passports.",
    url: "https://www.porteverglades.net/",
    schemaType: "CivicStructure",
  },
  {
    name: "Fort Lauderdale-Hollywood International Airport (FLL)",
    icon: "ri-flight-takeoff-line",
    distance: "15 minutes south via I-95 — approximately 12 miles from Pompano Beach",
    desc: "Primary international airport serving Pompano Beach and northern Broward County. Extensive connections to the Caribbean, Latin America, and major U.S. hubs.",
    url: "https://www.broward.org/airport/",
    schemaType: "Airport",
  },
  {
    name: "Miami International Airport (MIA)",
    icon: "ri-flight-takeoff-line",
    distance: "35–40 minutes south via I-95 — approximately 33 miles from Pompano Beach",
    desc: "Major international gateway with extensive long-haul routes to Europe, Asia, and South America. The preferred airport for Pompano Beach travelers on intercontinental itineraries.",
    url: "https://www.miami-airport.com/",
    schemaType: "Airport",
  },
  {
    name: "Pompano Beach Amphitheater",
    icon: "ri-music-line",
    distance: "0 miles — Pompano Beach Community Park",
    desc: "Popular outdoor concert venue hosting national touring acts and community events. A cultural anchor for Pompano Beach residents.",
    url: "https://www.pompanobeachfl.gov/",
    schemaType: "TouristAttraction",
  },
];

export default function PompanoHelpfulResourcesSection() {
  return (
    <section id="local-resources" className="py-16 md:py-20 bg-[#F8F9FB]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">Helpful Links</span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">Resources for Pompano Beach Travelers</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Broward County resources and nearby landmarks to help Pompano Beach residents gather everything they need.
          </p>
        </div>

        <div className="space-y-6">

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Broward County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Broward County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Broward County. Online, mail, or in-person options available.", url: "https://broward.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Broward County. Required for estate and legal matters.", url: "https://broward.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Broward County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.browardclerk.org/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Broward County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.browardclerk.org/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Broward County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Broward County Tax Collector — DMV Services Near Pompano Beach</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Florida driver&apos;s license or REAL ID, the Broward County Tax Collector handles DMV services for Pompano Beach residents.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        {loc.featured && (
                          <span className="inline-block text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full mt-1 mb-1">Primary</span>
                        )}
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.broward.county-taxes.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Broward County Tax Collector
                </a>
                <a href="https://www.flhsmv.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>FLHSMV — Florida DMV
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times at Broward County Tax Collector offices can exceed 2 hours. Book your slot online before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Nearby Landmarks */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-landscape-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Nearby Landmarks</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Key Landmarks &amp; Transportation Hubs Near Pompano Beach</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Pompano Beach sits along Florida&apos;s Gold Coast between Fort Lauderdale and Boca Raton. These nearby landmarks and transportation hubs define the area&apos;s travel patterns — from the Hillsboro Inlet providing direct ocean access to Port Everglades serving as one of the world&apos;s busiest cruise ports.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                {landmarks.map((landmark, i) => (
                  <a
                    key={i}
                    href={landmark.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 group-hover:bg-[#F6C344]/15 transition-colors">
                        <i className={`${landmark.icon} text-[#1A365D] text-sm`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{landmark.name}</p>
                        <p className="text-gray-400 text-[11px]">{landmark.distance}</p>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">{landmark.desc}</p>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international travel.</strong> Whether you are departing from Port Everglades on a Caribbean cruise, flying out of FLL to Latin America, or chartering a fishing boat through the Hillsboro Inlet to the Bahamas — a valid U.S. passport is mandatory. <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">Expedited processing through Fast Passport Center</a> is the fastest option for Pompano Beach residents.
                </p>
              </div>
            </div>
          </div>

          {/* Last Updated — visible freshness signal for AI/SEO crawlers */}
          <div className="text-center">
            <p className="text-gray-400 text-xs">
              <i className="ri-time-line mr-1"></i>
              Page last updated: <time dateTime="2026-07-14">July 14, 2026</time> — Pompano Beach passport services, nearby landmarks, and Broward County resources reviewed and refreshed regularly.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
}